import BurgerIcon from '@cointracker/styleguide/src/icons/burger.svg?react';
import CloseIcon from '@cointracker/styleguide/src/icons/close-light.svg?react';
import LogoIcon from '@cointracker/styleguide/src/icons/logo.svg?react';
import { REBRAND_URLS, URLS, useScrollListener } from '@cointracker/ui';
import { thirdPartyImagesURL } from '@cointracker/ui/src/utils/image-urls';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useCallback, useEffect, useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { Tags } from '../LoggedIn';
import { NavLink } from '../NavLink';
import ChevronDownIcon from '../icons/chevron-down.svg?react';
import { NavLinkText } from '../typography';
import { cn } from '../utils';
import { NavigationMenu, NavigationMenuItem } from './NavigationMenu';

const dropdownItemStyles = cn(
  'text-text-primary hover:bg-background-card-highlight cursor-pointer px-16 py-8 outline-none',
);
const dropdownContentStyles = cn(
  'rounded-8 border-line-card bg-background-pop-up text-text-primary shadow-elevation-1 max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] max-w-[280px] overflow-hidden border',
);

const TAX_PRO_UPGRADE_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSc5iAT3WnpltytA1xkX1pdv0brLd0jZ74Yej5gBsmnDQqEjjQ/viewform?usp=sf_link';

function setViewportHeight() {
  document.documentElement.style.setProperty(
    '--viewport-height',
    `${window.innerHeight}px`,
  );
}

interface TaxProTopBarProps {
  pathname?: string;
  userEmail?: string;
  onLogout?: () => void;
}

export const TaxProTopBar = ({ userEmail, onLogout }: TaxProTopBarProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollThreshold = 100; // safe number between 70 (desktop header height) and 96 (mobile header height)

  useScrollListener(() => {
    if (window.scrollY < scrollThreshold) {
      setIsVisible(true);
    } else if (window.scrollY > lastScrollY) {
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }
    setLastScrollY(window.scrollY);
  });

  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const onToggleDropdown = useCallback(() => {
    setDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen]);

  useEffect(() => {
    setViewportHeight();
    window.addEventListener('resize', setViewportHeight);
    return () => {
      window.removeEventListener('resize', setViewportHeight);
    };
  }, []);

  const isQuebecDemo = window.location.pathname.includes('quebec-demo');
  const isSpainDemo = window.location.pathname.includes('spain-demo');
  const isGovDemo = window.location.pathname.includes('gov-demo');
  if (isQuebecDemo || isSpainDemo || isGovDemo) {
    return (
      <header
        className={cn(
          'bg-grey-0 dark:bg-slate-90 z-99 duration-250 fixed left-0 right-0 top-0 flex max-w-full flex-wrap items-center transition-transform ease-out',
          { '-translate-y-full': !isVisible },
        )}
      >
        <div className="bg-grey-0 dark:bg-slate-90 relative flex h-[78px] w-full flex-wrap items-center gap-x-24 px-16 md:h-[68px] md:px-32">
          <div className="flex flex-1 items-center gap-10">
            {isQuebecDemo ? (
              <img
                src={`${thirdPartyImagesURL}/quebeclogo.png`}
                alt="Revenue Quebec Logo"
                className="absolute left-32 top-10 h-80 w-[100px]"
              />
            ) : isSpainDemo ? (
              <img
                src={`${thirdPartyImagesURL}/spainlogo.png`}
                alt="Revenue Spain Logo"
                className="absolute left-32 top-10 h-80 w-[100px]"
              />
            ) : (
              <>
                <a href={REBRAND_URLS.HOME}>
                  <LogoIcon className="text-text-primary block" />
                </a>
                <Tags color="black" filled condensed>
                  Investigator
                </Tags>
              </>
            )}
          </div>
          <NavigationMenu>
            <NavigationMenuItem className="hidden lg:block">
              <NavLink
                href={`${isQuebecDemo ? '/quebec-demo' : '/spain-demo'}/tax-pro-view`}
              >
                Cases
              </NavLink>
            </NavigationMenuItem>
            <NavigationMenuItem className="hidden lg:block">
              <NavLink href={URLS.SUPPORT} target="_blank">
                Support
              </NavLink>
            </NavigationMenuItem>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="hidden lg:block">
                <div className="text-text-secondary flex items-center gap-2">
                  <NavLinkText className="!text-text-secondary">
                    {userEmail?.toLowerCase() || ''}
                  </NavLinkText>
                  <ChevronDownIcon />
                </div>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className={dropdownContentStyles}
                  style={{ zIndex: 100 }}
                >
                  <DropdownMenu.Item className={dropdownItemStyles}>
                    <a href={URLS.EDIT_USER_PROFILE}>Settings</a>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item className={dropdownItemStyles}>
                    <a
                      href={TAX_PRO_UPGRADE_URL}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Upgrade account
                    </a>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className={dropdownItemStyles}
                    onClick={onLogout}
                  >
                    Logout
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <button
              className="text-text-primary flex cursor-pointer border-0 bg-transparent lg:hidden"
              onClick={onToggleDropdown}
              aria-label="burger-menu-button"
            >
              {!isDropdownOpen && <BurgerIcon />}
              {isDropdownOpen && <CloseIcon width="30px" height="30px" />}
            </button>
          </NavigationMenu>
        </div>
        <Collapsible.Collapsible
          open={isDropdownOpen}
          onOpenChange={onToggleDropdown}
          className="bg-grey-5 dark:bg-slate-70 block w-full lg:hidden"
          data-testid="collapsible-container"
        >
          <Collapsible.CollapsibleContent>
            <RemoveScroll enabled={isDropdownOpen}>
              <div className="basis-full">
                <div className="flex h-[calc(var(--viewport-height)-80px)] flex-col overflow-auto overscroll-contain p-16 md:h-[calc(var(--viewport-height)-68px)] md:p-32">
                  <div className="mt-40 flex flex-col gap-32 md:mt-48">
                    <NavLink href={URLS.VIEW_TAX_PRO_DASHBOARD}>
                      Dashboard
                    </NavLink>
                    <NavLink
                      href="https://feedback.cointracker.io/new-posts"
                      target="_blank"
                    >
                      Request a Feature
                    </NavLink>
                    <NavLink href={URLS.EDIT_USER_PROFILE}>Settings</NavLink>
                    <NavLink href={URLS.TAX_PAGE}>
                      Go to personal account
                    </NavLink>
                    <NavLink href={TAX_PRO_UPGRADE_URL} target="_blank">
                      Upgrade account
                    </NavLink>
                    <NavLink
                      href="https://support.cointracker.io/hc/en-us"
                      target="_blank"
                    >
                      Tax pro support
                    </NavLink>
                    <NavLink onClick={onLogout}>Sign out</NavLink>
                  </div>
                </div>
              </div>
            </RemoveScroll>
          </Collapsible.CollapsibleContent>
        </Collapsible.Collapsible>
      </header>
    );
  }

  return (
    <header
      className={cn(
        'bg-grey-0 dark:bg-slate-90 z-99 duration-250 fixed left-0 right-0 top-0 flex max-w-full flex-wrap items-center transition-transform ease-out',
        { '-translate-y-full': !isVisible },
      )}
    >
      <div className="bg-grey-0 dark:bg-slate-90 relative flex h-[78px] w-full flex-wrap items-center gap-x-24 px-16 md:h-[68px] md:px-32">
        <div className="flex flex-1 items-center gap-10">
          <a href={REBRAND_URLS.HOME}>
            <LogoIcon className="text-text-primary block" />
          </a>
          <Tags color="black" filled condensed>
            Tax Pros
          </Tags>
        </div>
        <NavigationMenu>
          <NavigationMenuItem className="hidden lg:block">
            <NavLink href={URLS.VIEW_TAX_PRO_DASHBOARD}>Dashboard</NavLink>
          </NavigationMenuItem>
          <NavigationMenuItem className="hidden lg:block">
            <NavLink
              href="https://feedback.cointracker.io/new-posts"
              target="_blank"
            >
              Request a Feature
            </NavLink>
          </NavigationMenuItem>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger className="hidden lg:block">
              <div className="text-text-secondary flex items-center gap-2">
                <NavLinkText className="!text-text-secondary">
                  {userEmail?.toLowerCase() || ''}
                </NavLinkText>
                <ChevronDownIcon />
              </div>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className={dropdownContentStyles}
                style={{ zIndex: 100 }}
              >
                <DropdownMenu.Item className={dropdownItemStyles}>
                  <a href={URLS.TAX_PAGE}>Go to personal account</a>
                </DropdownMenu.Item>
                <DropdownMenu.Item className={dropdownItemStyles}>
                  <a href={URLS.EDIT_USER_PROFILE}>Settings</a>
                </DropdownMenu.Item>
                <DropdownMenu.Item className={dropdownItemStyles}>
                  <a
                    href={TAX_PRO_UPGRADE_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Upgrade account
                  </a>
                </DropdownMenu.Item>
                <DropdownMenu.Item className={dropdownItemStyles}>
                  <a
                    href="https://support.cointracker.io/hc/en-us"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Tax pro support
                  </a>
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className={dropdownItemStyles}
                  onClick={onLogout}
                >
                  Logout
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          <button
            className="text-text-primary flex cursor-pointer border-0 bg-transparent lg:hidden"
            onClick={onToggleDropdown}
            aria-label="burger-menu-button"
          >
            {!isDropdownOpen && <BurgerIcon />}
            {isDropdownOpen && <CloseIcon width="30px" height="30px" />}
          </button>
        </NavigationMenu>
      </div>
      <Collapsible.Collapsible
        open={isDropdownOpen}
        onOpenChange={onToggleDropdown}
        className="bg-grey-5 dark:bg-slate-70 block w-full lg:hidden"
        data-testid="collapsible-container"
      >
        <Collapsible.CollapsibleContent>
          <RemoveScroll enabled={isDropdownOpen}>
            <div className="basis-full">
              <div className="flex h-[calc(var(--viewport-height)-80px)] flex-col overflow-auto overscroll-contain p-16 md:h-[calc(var(--viewport-height)-68px)] md:p-32">
                <div className="mt-40 flex flex-col gap-32 md:mt-48">
                  <NavLink href={URLS.VIEW_TAX_PRO_DASHBOARD}>
                    Dashboard
                  </NavLink>
                  <NavLink
                    href="https://feedback.cointracker.io/new-posts"
                    target="_blank"
                  >
                    Request a Feature
                  </NavLink>
                  <NavLink href={URLS.EDIT_USER_PROFILE}>Settings</NavLink>
                  <NavLink href={URLS.TAX_PAGE}>Go to personal account</NavLink>
                  <NavLink href={TAX_PRO_UPGRADE_URL} target="_blank">
                    Upgrade account
                  </NavLink>
                  <NavLink
                    href="https://support.cointracker.io/hc/en-us"
                    target="_blank"
                  >
                    Tax pro support
                  </NavLink>
                  <NavLink onClick={onLogout}>Sign out</NavLink>
                </div>
              </div>
            </div>
          </RemoveScroll>
        </Collapsible.CollapsibleContent>
      </Collapsible.Collapsible>
    </header>
  );
};
